"use client"

import React, { useState, useEffect } from "react"
import { Product } from "./EditSaleModal"
import { api } from "../../services/Axios"
import Modal from "../ui/Modal"
import Button from "../ui/Button"
import Input from "../ui/Input"
import { X, Search, Save } from "lucide-react"

interface AddProductModalProps {
  sucursalId: string
  isOpen: boolean
  onClose: (selectedProducts: Product[]) => void
}

interface SelectedProduct extends Product {
  cantidadSeleccionada: number
}

const AddProductModal: React.FC<AddProductModalProps> = ({ sucursalId, isOpen, onClose }) => {
  const [productos, setProductos] = useState<Product[]>([])
  const [filteredProductos, setFilteredProductos] = useState<Product[]>([])
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([])
  const [searchTerm, setSearchTerm] = useState<string>("")

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        const response = await api.get(`/api/productos?sucursal=${sucursalId}&limit=0`)
        const data = response.data?.productos || []
        setProductos(Array.isArray(data) ? data : [])
        setFilteredProductos(Array.isArray(data) ? data : [])
      } catch (error) {
        console.error("Error al cargar productos:", error)
        setProductos([])
        setFilteredProductos([])
      }
    }
    if (isOpen) fetchProductos()
  }, [isOpen, sucursalId])

  const toggleProductSelection = (producto: Product) => {
    setSelectedProducts(prevSelected => {
      const existingProduct = prevSelected.find(p => p._id === producto._id)
      if (existingProduct) {
        return prevSelected.map(p =>
          p._id === producto._id
            ? { ...p, cantidadSeleccionada: p.cantidadSeleccionada + 1 }
            : p
        )
      } else {
        return [...prevSelected, { ...producto, cantidadSeleccionada: 1 }]
      }
    })
  }

  const handleClose = () => {
    const selectedWithCantidad = selectedProducts.map(p => ({
      ...p,
      cantidad: p.cantidadSeleccionada,
    }))
    onClose(selectedWithCantidad)
  }

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase()
    setSearchTerm(term)
    setFilteredProductos(
      productos.filter(producto =>
        producto.nombre.toLowerCase().includes(term)
      )
    )
  }

  const clearSelection = () => {
    setSelectedProducts([])
    setSearchTerm("")
    setFilteredProductos(productos)
  }

  if (!isOpen) return null

  return (
    <Modal isOpen={isOpen} onClose={() => onClose([])} title="Seleccionar Productos" size="md">
      {/* Contenido del Modal */}
      <div className="space-y-6">
        {/* Barra de Búsqueda */}
        <div className="flex items-center space-x-3">
          <div className="relative w-full">
            <Input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={handleFilterChange}
              fullWidth
              className="pr-10"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
          </div>
          <Button variant="outline" onClick={clearSelection}>
            <X className="w-4 h-4 mr-1" /> Limpiar
          </Button>
        </div>

        {/* Lista de Productos */}
        <div className="grid grid-cols-1 gap-3" style={{ maxHeight: "40vh", overflowY: "auto" }}>
          {filteredProductos.length > 0 ? (
            filteredProductos.map(producto => {
              const selectedProduct = selectedProducts.find(p => p._id === producto._id)
              const cantidadSeleccionada = selectedProduct ? selectedProduct.cantidadSeleccionada : 0

              return (
                <div
                  key={producto._id}
                  onClick={() => toggleProductSelection(producto)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                    cantidadSeleccionada > 0 ? "bg-green-50 border-green-300" : "border-gray-200"
                  }`}
                >
                  <span className="block text-gray-800 font-medium">
                    {producto.nombre} - ${producto.valorVenta.toLocaleString()}
                  </span>
                  {cantidadSeleccionada > 0 && (
                    <span className="block text-sm text-green-600 mt-1">
                      Seleccionado: {cantidadSeleccionada}
                    </span>
                  )}
                </div>
              )
            })
          ) : (
            <p className="text-gray-500">No se encontraron productos</p>
          )}
        </div>
      </div>

      {/* Footer del Modal */}
      <div className="flex justify-end space-x-3 mt-6">
        <Button variant="outline" onClick={() => onClose([])}>
          <X className="w-4 h-4 mr-1" /> Cancelar
        </Button>
        <Button onClick={handleClose}>
          <Save className="w-4 h-4 mr-1" /> Agregar {selectedProducts.length} Producto(s)
        </Button>
      </div>
    </Modal>
  )
}

export default AddProductModal
